import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { CSSProperties } from 'react';
import { useIntl } from 'react-intl';

import { Scrollable } from '@sorare/core/src/atoms/navigation/Scrollable';
import { fantasy } from '@sorare/core/src/lib/glossary';

import { DivisionChangeConfigBar } from '#src/components/leaderboard/DivisionChangeConfigBar/index.tsx';
import { LiveStreakProgress } from '#src/components/leaderboard/LiveStreakProgress/index.tsx';
import { isFixtureLive } from '#src/lib/so5.ts';

import type {
  TargetsCarousel_so5Leaderboard,
  TargetsCarousel_so5Lineup,
} from './__generated__/index.graphql.ts';

type Props = {
  so5Leaderboard: TargetsCarousel_so5Leaderboard;
  so5Lineup?: TargetsCarousel_so5Lineup;
};

export const TargetsCarousel = ({ so5Leaderboard, so5Lineup }: Props) => {
  const { formatMessage } = useIntl();
  const displayStreakProgressBar =
    !!so5Lineup?.myThresholdsStreakTask &&
    isFixtureLive(so5Leaderboard.so5Fixture);
  const hasDivisionChangeConfiguration =
    !!so5Leaderboard.divisionChangeConfiguration;

  const shouldDisplayDots =
    displayStreakProgressBar && hasDivisionChangeConfiguration;

  if (!displayStreakProgressBar && !hasDivisionChangeConfiguration) {
    return null;
  }

  return (
    <div
      style={
        {
          '--dots-bottom': '-20px',
          paddingBottom: shouldDisplayDots ? 'var(--double-unit)' : '0',
        } as CSSProperties
      }
    >
      <Scrollable
        itemToDisplay={1}
        dots={[
          displayStreakProgressBar
            ? formatMessage(fantasy.hotStreak)
            : undefined,
          hasDivisionChangeConfiguration
            ? formatMessage(fantasy.promotion)
            : undefined,
        ].filter(Boolean)}
      >
        {!!displayStreakProgressBar && (
          <LiveStreakProgress so5Lineup={so5Lineup} />
        )}
        {!!hasDivisionChangeConfiguration && (
          <DivisionChangeConfigBar
            so5Leaderboard={so5Leaderboard}
            so5Lineup={so5Lineup}
          />
        )}
      </Scrollable>
    </div>
  );
};

TargetsCarousel.fragments = {
  so5Lineup: gql`
    fragment TargetsCarousel_so5Lineup on So5Lineup {
      id
      myThresholdsStreakTask {
        id
      }
      ...LiveStreakProgress_so5Lineup
      ...DivisionChangeConfigBar_so5Lineup
    }
    ${LiveStreakProgress.fragments.so5Lineup}
    ${DivisionChangeConfigBar.fragments.so5Lineup}
  ` as TypedDocumentNode<TargetsCarousel_so5Lineup>,
  so5Leaderboard: gql`
    fragment TargetsCarousel_so5Leaderboard on So5Leaderboard {
      slug
      so5Fixture {
        slug
        ...isFixtureLive_so5Fixture
      }
      divisionChangeConfiguration {
        id
      }
      ...DivisionChangeConfigBar_so5Leaderboard
    }
    ${DivisionChangeConfigBar.fragments.so5Leaderboard}
    ${isFixtureLive.fragments.so5Fixture}
  ` as TypedDocumentNode<TargetsCarousel_so5Leaderboard>,
};
